import React from "react"

import { useStaticQuery, graphql } from "gatsby"
import { GatsbyImage, getImage } from "gatsby-plugin-image"

import PageSection from "../../pageSection/pageSection.js"
import H2 from "../../h2/h2.js"
import H3 from "../../h3/h3.js"
import P from "../../p/p.js"

import styles from "./howTillWorks.module.scss"

import dollarCircleBlue from "../../../images/home/dollar-circle-blue.svg"
import dollarCircleGreen from "../../../images/home/dollar-circle-green.svg"
import dollarCirclePurple from "../../../images/home/dollar-circle-purple.svg"
import dollarCircleYellow from "../../../images/home/dollar-circle-yellow.svg"

import leanOnPhone from "../../../images/home/lean-on-phone-illustration.png"
import phoneFrame from "../../../images/home/phone-frame.png"
import spendBalance from "../../../images/home/spend-balance-screen.png"
import savingsGoal from "../../../images/home/goal-screen.png"
import quickGive from "../../../images/home/quick-give-screen.png"
import appleWallet from "../../../images/home/apple-wallet-screen.png"

const HowTillWorksSection = () => {
  const data = useStaticQuery(graphql`
    query {
      leanOnPhone: file(relativePath: { eq: "home/lean-on-phone-illustration.png" }) {
        childImageSharp {
          gatsbyImageData (
            width: 770
            placeholder: NONE
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
      phoneFrame: file(relativePath: { eq: "home/phone-frame.png" }) {
        childImageSharp {
          gatsbyImageData (
            width: 350
            placeholder: NONE
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
      spendBalance: file(relativePath: { eq: "home/spend-balance-screen.png" }) {
        childImageSharp {
          gatsbyImageData (
            width: 330
            placeholder: NONE
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
    }
  `)

  const [visibleImage, setVisibleImage] = React.useState("target-1")

  React.useLayoutEffect(() => {
    let options = {
      root: document.querySelector('#scroll-area'),
      rootMargin: '0px',
      threshold: 1.0
    }

    let callback = (entries, observer) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          return
        }
        if (!entry.target) {
          return
        }

        if (entry.target.id === "list-item-1") {
          setVisibleImage("target-1")
        } else if (entry.target.id === "list-item-2") {
          setVisibleImage("target-2")
        } else if (entry.target.id === "list-item-3") {
          setVisibleImage("target-3")
        } else if (entry.target.id === "list-item-4") {
          setVisibleImage("target-4")
        }
      });
    };

    const observer = new IntersectionObserver(callback, options)

    let target1 = document.querySelector('#list-item-1');
    observer.observe(target1);

    let target2 = document.querySelector('#list-item-2');
    observer.observe(target2);

    let target3 = document.querySelector('#list-item-3');
    observer.observe(target3);

    let target4 = document.querySelector('#list-item-4');
    observer.observe(target4);
  }, [])

  return (
    <PageSection backgroundColor="#ffffff">
      <div className={styles.contentContainer}>
        <div className={styles.headingContainer}>
          <div className={styles.headingCopyContainer}>
            <H2 slideUp>How Till Works</H2>
            <div className={styles.yellowBar} />
            <P className={styles.p}>
              It’s simple really. We help families prepare kids for the reality of money management in life: how to earn, spend, and save.
            </P>
          </div>

          <div className={[styles.imgContainer, styles.hideLarge].join(" ")}>
            <div className={styles.innerImgContainer}>
              <GatsbyImage
                image={getImage(data.leanOnPhone)}
                alt="Illustrated image of a student leaning against the wall with colorful circles in the background"
                width={770}
              />

              <div className={styles.phoneFrameContainer}>
                <GatsbyImage
                  image={getImage(data.phoneFrame)}
                  alt="Phone with a blank screen"
                  width={350}
                  className={styles.phoneFrame}
                />

                <div style={{ position: "absolute", top: 0, left: 0, padding: 3 }}>
                  <GatsbyImage
                    image={getImage(data.spendBalance)}
                    alt="Till App screenshot showing the available balance and the spending balance on a Till debit card account"
                    width={330}
                    className={styles.screen}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", position: "relative" }}>
          <div className={styles.fancyScrollContainer}>
            <div className={styles.scroller} id="scroll-area">
              <ul className={styles.listRoot}>
                <li className={styles.listItem} id="list-item-1" tabindex="0">
                  <img
                    className={styles.dollarCircleImg}
                    src={dollarCirclePurple}
                    alt="Purple circle with white dollar sign overlayed on it"
                  />
                  <H3 className={styles.h3}>A real-world foundation, built on spending</H3>
                  <P className={styles.p}>
                    It’s not all save, save, save. Till makes it so kids can learn how to manage money in real-life situations. With Till, kids and parents get concrete insights into spending behavior and see progress in real time.
                  </P>
                </li>
                <li className={styles.listItem} id="list-item-2" tabindex="0">
                  <img
                    className={styles.dollarCircleImg}
                    src={dollarCircleGreen}
                    alt="Green circle with white dollar sign overlayed on it"
                  />
                  <H3 className={styles.h3}>Set goals to save. See the progress</H3>
                  <P className={styles.p}>It’s about learning to make tradeoffs and saving <em>while</em> you spend. With Till, kids can set savings goals large and small—from video game consoles to their college fund.</P>
                </li>
                <li className={styles.listItem} id="list-item-3" tabindex="0">
                  <img
                    className={styles.dollarCircleImg}
                    src={dollarCircleYellow}
                    alt="Yellow circle with white dollar sign overlayed on it"
                  />
                  <H3 className={styles.h3}>Power-up kids’ earning</H3>
                  <P className={styles.p}>
                    From everyday tasks to direct deposit from a part-time job, Till helps kids manage their earnings all in one place.
                  </P>
                </li>
                <li className={styles.listItem}  tabindex="0">
                  <img
                    className={styles.dollarCircleImg}
                    src={dollarCircleBlue}
                    alt="Blue circle with white dollar sign overlayed on it"
                  />
                  <H3 className={styles.h3}>Spend securely</H3>
                  <P className={styles.p}>
                    With a virtual and physical debit card kids are covered. No need to worry about losing cash, and if they misplace the physical card, you can freeze it with one tap.
                  </P>
                  <div id="list-item-4" />
                </li>
              </ul>
            </div>
          </div>
          <div style={{ width: "48%", overflow: "hidden", margin: "0 0 0 auto" }} className={styles.showLarge}>
            <div className={styles.innerImgContainer}>
              <img
                src={leanOnPhone}
                alt="Illustrated image of a student leaning against the wall with colorful circles in the background"
              />

              <div className={styles.phoneFrameContainer}>
                <img
                  src={phoneFrame}
                  alt="Phone with a blank screen"
                  className={styles.phoneFrame}
                />

                <div id="test-1" style={{ position: "absolute", top: 0, left: 0, padding: 5, display: (visibleImage === "target-1" ? "block" : "none") }}>
                  <img src={spendBalance} alt="Screenshot of the Till Financial app showing account spending" className={styles.screen} />
                </div>
                <div id="test-2" style={{ position: "absolute", top: 0, left: 0, padding: 5, display: (visibleImage === "target-2" ? "block" : "none") }}>
                  <img src={savingsGoal} alt="Screenshot of the Till Financial app showing a saving goals contribution breakdown" className={styles.screen} />
                </div>
                <div id="test-3" style={{ position: "absolute", top: 0, left: 0, padding: 5, display: (visibleImage === "target-3" ? "block" : "none") }}>
                  <img src={quickGive} alt="Screenshot of the Till Financial app showing how to send money to a kid's account" className={styles.screen} />
                </div>
                <div id="test-4" style={{ position: "absolute", top: 0, left: 0, padding: 5, display: (visibleImage === "target-4" ? "block" : "none") }}>
                  <img src={appleWallet} alt="Smartphone showing the Till card added as a virtual payment card" className={styles.screen} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageSection >
  )
}

export { HowTillWorksSection }
